/* Hulk Discount → Quantity sync (Empire-ready) with manual override
   - On Hulk tier radio select, set product quantity to the tier’s minimum.
   - If shopper manually edits quantity (typing or +/−), respect THEIR value on Add to cart.
   - On Add to cart: only bump to tier min when no manual override has happened.
*/
(function () {
  if (window.__hulkQtySync) return; window.__hulkQtySync = true;

  // ----- per-form state to detect manual overrides -----
  const formState = new WeakMap(); // form -> { userOverride: boolean, lastEditAt: number }

  const getState = (form) => {
    let s = formState.get(form);
    if (!s) { s = { userOverride: false, lastEditAt: 0 }; formState.set(form, s); }
    return s;
  };
  const setOverride = (form, on) => { const s = getState(form); s.userOverride = !!on; s.lastEditAt = Date.now(); };

  // ----- helpers -----
  const getAllQtyInputs = (form) => {
    const set = new Set();
    form.querySelectorAll(`
      input[name="quantity"],
      input[type="hidden"][name="quantity"],
      .quantity-selector__input,
      [data-quantity-input],
      quantity-input input,
      input.quantity-selector__input,
      input[type="number"]
    `).forEach(n => set.add(n));
    return Array.from(set);
  };

  const readCurrentQty = (form) => {
    const first = getAllQtyInputs(form)[0];
    const n = parseInt(first?.value || first?.getAttribute?.('value') || '1', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  const syncQty = (form, val) => {
    const q = Math.max(1, parseInt(val || "1", 10));
    getAllQtyInputs(form).forEach(inp => {
      try {
        inp.value = q;
        inp.setAttribute('value', String(q));  // some scripts read the attribute on submit
        inp.dispatchEvent(new Event('input',  { bubbles: true }));
        inp.dispatchEvent(new Event('change', { bubbles: true }));
      } catch {}
    });
    form.dispatchEvent(new CustomEvent('quantity-sync', { bubbles: true, detail: { value: q }}));
    return q;
  };

  const getHulkWidget = (root) =>
    root.querySelector('.hulkapps-volumes, .hulkapps-vd, .hulkapps-volume-pricing, .hulkapps-volume-discount-tiers');

  // Read minimum qty from multiple places (robust)
  const readTierQty = (radio) => {
    // 1) data-min-qty on any ancestor
    const holder = radio.closest('[data-min-qty]');
    if (holder?.dataset?.minQty) {
      const n = parseInt(holder.dataset.minQty, 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
    // 2) radio's own value if numeric (many widgets use value="5")
    if (radio.value) {
      const n = parseInt(radio.value, 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
    // 3) first integer in label text (e.g., "Buy 10 for $...")
    const label = radio.closest('label');
    if (label) {
      const m = (label.textContent || '').replace(/\s+/g, ' ').match(/\b(\d{1,6})\b/);
      if (m) {
        const n = parseInt(m[1], 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
    return 1;
  };

  // Optional: fetch the price text within the offer block (if you need it for logging/UI)
  const readTierPriceText = (radio) => {
    const offer = radio.closest('.offer-option-item');
    const priceEl = offer?.querySelector('.offer-option .money, .offer-option_price .money, .money');
    return (priceEl?.textContent || '').trim();
  };

  const wireWidget = (widget) => {
    if (widget.__hulkQtyWired) return;
    widget.__hulkQtyWired = true;

    // find the associated product form
    const form = widget.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const applySelected = () => {
      const sel = widget.querySelector('input[type="radio"]:checked');
      if (!sel) return;
      const q = readTierQty(sel);
      // When a tier is chosen, we consider that the shopper *accepts* that min qty (until they manually override later)
      setOverride(form, false);
      syncQty(form, q);
    };

    // 1) On radio change → set qty to tier min, clear manual override
    widget.addEventListener('change', (e) => {
      const r = e.target.closest('input[type="radio"]');
      if (!r || !widget.contains(r)) return;
      setOverride(form, false);
      syncQty(form, readTierQty(r));
      // console.debug('Tier price:', readTierPriceText(r)); // optional
    });

    // 2) On label/cell click → selection might change after click; apply after tick
    widget.addEventListener('click', (e) => {
      if (e.target.closest('.offer-option-item, label, .offer-option_txt, .offer-option_price')) {
        setTimeout(applySelected, 0);
      }
    });

    // 3) Apply on first load (preselected tier)
    applySelected();

    // 4) Re-apply if Hulk re-renders the widget
    const mo = new MutationObserver(() => setTimeout(applySelected, 0));
    mo.observe(widget, { childList: true, subtree: true });

    // 5) Detect manual quantity edits (+/− or typing) → set manual override
    //    a) direct input typing
    getAllQtyInputs(form).forEach(inp => {
      inp.addEventListener('input',  () => setOverride(form, true));
      inp.addEventListener('change', () => setOverride(form, true));
    });
    //    b) theme +/− buttons living inside the form
    form.addEventListener('click', (e) => {
      if (e.target.closest('[data-quantity-plus],[data-quantity-minus]')) {
        // mark override now; value will update right after this tick
        setOverride(form, true);
        setTimeout(() => {}, 0);
      }
    });
  };

  const init = () => {
    // Wire all current widgets
    const root = document.querySelector('#MainContent') || document.body;
    root.querySelectorAll('.hulkapps-volumes, .hulkapps-vd, .hulkapps-volume-pricing, .hulkapps-volume-discount-tiers')
        .forEach(wireWidget);

    // Re-wire when sections/quick views load
    const mo = new MutationObserver(() => {
      root.querySelectorAll('.hulkapps-volumes, .hulkapps-vd, .hulkapps-volume-pricing, .hulkapps-volume-discount-tiers')
          .forEach(wireWidget);
    });
    mo.observe(root, { childList: true, subtree: true });

    // 🔸 Just-in-time logic on Add to cart:
    // If a Hulk tier is selected but the shopper manually edited qty, respect their number.
    // Otherwise (no manual override), ensure qty is at least the tier minimum.
    document.addEventListener('click', (e) => {
      const atc = e.target.closest('button[name="add"], [data-add-to-cart], [data-product-atc]');
      if (!atc) return;
      const form = atc.form || atc.closest('form[action*="/cart/add"]');
      if (!form) return;

      const container = form.closest('.product, .product-page, #MainContent') || document;
      const widget = getHulkWidget(container);
      if (!widget) return;

      const sel = widget.querySelector('input[type="radio"]:checked');
      if (!sel) return;

      const state = getState(form);
      const currentQty = readCurrentQty(form);
      const tierMin = readTierQty(sel);

      // If the user manually edited qty, do NOT override it—let them add any amount (even if not a tier)
      if (state.userOverride) {
        // nothing to do; if they typed 2 and the tier min is 5, we'll add 2 (no discount, as intended)
        return;
      }

      // No manual override: only enforce the tier minimum if current qty is below it
      if (currentQty < tierMin) {
        syncQty(form, tierMin);
      }
      // let the native submit proceed
    }, { capture: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
